import { Link } from 'react-router-dom';

interface RsvpRecord {
  id: string;
  event_title: string;
  event_date: string;
  event_time: string;
  event_location: string;
  ticket_type: 'general' | 'vip';
  quantity: number;
  total_amount: number;
  payment_status: string;
  created_at: string;
}

interface RsvpCardProps {
  rsvp: RsvpRecord;
  isPast?: boolean;
  isVipMember?: boolean;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function getDaysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const event = new Date(dateStr + 'T00:00:00');
  return Math.round((event.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function buildCalendarUrl(rsvp: RsvpRecord): string {
  const dateClean = rsvp.event_date.replace(/-/g, '');
  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(rsvp.event_title)}&dates=${dateClean}/${dateClean}&location=${encodeURIComponent(rsvp.event_location)}`;
}

export default function RsvpCard({ rsvp, isPast = false, isVipMember = false }: RsvpCardProps) {
  const daysUntil = getDaysUntil(rsvp.event_date);
  const isVip = rsvp.ticket_type === 'vip';
  const isFree = rsvp.total_amount === 0;

  let urgencyBadge: { label: string; color: string } | null = null;
  if (!isPast) {
    if (daysUntil === 0) urgencyBadge = { label: 'Today!', color: 'bg-rose-500 text-white' };
    else if (daysUntil === 1) urgencyBadge = { label: 'Tomorrow', color: 'bg-orange-400 text-white' };
    else if (daysUntil <= 7) urgencyBadge = { label: `${daysUntil}d away`, color: 'bg-pink-100 text-pink-700' };
  }

  return (
    <div className={`bg-white rounded-2xl border transition-all ${isPast ? 'border-gray-100 opacity-70' : isVipMember ? 'border-amber-200 hover:border-amber-300' : 'border-gray-200 hover:border-pink-200'}`}>
      {/* Top accent — golden for VIP members, pink for regular */}
      <div className={`h-1.5 w-full rounded-t-2xl ${isVipMember ? 'bg-gradient-to-r from-amber-400 via-rose-400 to-pink-500' : isVip ? 'bg-gradient-to-r from-rose-400 to-pink-500' : 'bg-gradient-to-r from-pink-300 to-rose-400'}`} />

      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex-1 min-w-0">
            {/* Badges row */}
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              {/* TMM VIP Member badge — shows membership status */}
              {isVipMember && (
                <span className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-100 to-rose-100 border border-amber-200 text-amber-700 text-[10px] font-extrabold px-2.5 py-1 rounded-full">
                  <i className="ri-vip-crown-2-fill text-xs text-amber-500" />
                  TMM VIP
                </span>
              )}
              {/* Ticket-type VIP badge */}
              {isVip && !isVipMember && (
                <span className="inline-flex items-center gap-1 bg-rose-100 text-rose-600 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  <i className="ri-vip-crown-fill text-xs" />
                  VIP Ticket
                </span>
              )}
              {isVip && isVipMember && (
                <span className="inline-flex items-center gap-1 bg-rose-50 text-rose-500 text-[10px] font-bold px-2 py-0.5 rounded-full">
                  <i className="ri-vip-crown-fill text-xs" />
                  VIP Ticket
                </span>
              )}
              {urgencyBadge && (
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${urgencyBadge.color}`}>
                  <i className="ri-time-fill text-xs" />
                  {urgencyBadge.label}
                </span>
              )}
              {isPast && (
                <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-500 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                  <i className="ri-checkbox-circle-line text-xs" />
                  Attended
                </span>
              )}
            </div>
            <h3 className="text-base font-bold text-gray-900 leading-snug truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
              {rsvp.event_title}
            </h3>
          </div>

          {/* Status pill */}
          <div className={`shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap ${
            rsvp.payment_status === 'paid'
              ? 'bg-emerald-100 text-emerald-700'
              : rsvp.payment_status === 'free'
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-amber-100 text-amber-700'
          }`}>
            {rsvp.payment_status === 'paid' || rsvp.payment_status === 'free' ? (
              <span className="flex items-center gap-1">
                <i className="ri-checkbox-circle-fill text-xs" />
                Confirmed
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <i className="ri-time-line text-xs" />
                Pending
              </span>
            )}
          </div>
        </div>

        {/* VIP member perks reminder strip */}
        {isVipMember && !isPast && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mb-3">
            <i className="ri-shield-star-fill text-amber-500 text-sm shrink-0" />
            <p className="text-[10px] text-amber-700 font-semibold leading-tight">
              Early access reserved · No RSVP fee applies · VIP perks active
            </p>
          </div>
        )}

        {/* Event details grid */}
        <div className="grid grid-cols-2 gap-2.5 mb-4">
          <div className="flex items-start gap-2">
            <div className={`w-6 h-6 flex items-center justify-center ${isVipMember ? 'bg-amber-50' : 'bg-pink-50'} rounded-lg shrink-0`}>
              <i className={`ri-calendar-line ${isVipMember ? 'text-amber-500' : 'text-pink-500'} text-xs`} />
            </div>
            <div>
              <div className="text-[10px] text-gray-400 font-medium">Date</div>
              <div className="text-xs font-semibold text-gray-800 leading-tight">{formatDate(rsvp.event_date)}</div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className={`w-6 h-6 flex items-center justify-center ${isVipMember ? 'bg-amber-50' : 'bg-pink-50'} rounded-lg shrink-0`}>
              <i className={`ri-time-line ${isVipMember ? 'text-amber-500' : 'text-pink-500'} text-xs`} />
            </div>
            <div>
              <div className="text-[10px] text-gray-400 font-medium">Time</div>
              <div className="text-xs font-semibold text-gray-800 leading-tight">{rsvp.event_time}</div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className={`w-6 h-6 flex items-center justify-center ${isVipMember ? 'bg-amber-50' : 'bg-pink-50'} rounded-lg shrink-0`}>
              <i className={`ri-map-pin-line ${isVipMember ? 'text-amber-500' : 'text-pink-500'} text-xs`} />
            </div>
            <div>
              <div className="text-[10px] text-gray-400 font-medium">Location</div>
              <div className="text-xs font-semibold text-gray-800 leading-tight line-clamp-1">{rsvp.event_location}</div>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className={`w-6 h-6 flex items-center justify-center ${isVipMember ? 'bg-amber-50' : 'bg-pink-50'} rounded-lg shrink-0`}>
              <i className={`ri-ticket-2-line ${isVipMember ? 'text-amber-500' : 'text-pink-500'} text-xs`} />
            </div>
            <div>
              <div className="text-[10px] text-gray-400 font-medium">Tickets</div>
              <div className="text-xs font-semibold text-gray-800 leading-tight">
                {rsvp.quantity} × {isVip ? 'VIP' : 'GA'} · {isFree ? 'Free' : `$${rsvp.total_amount}`}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        {!isPast && (
          <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
            <a
              href={buildCalendarUrl(rsvp)}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 ${isVipMember ? 'bg-amber-500 hover:bg-amber-600' : 'bg-pink-500 hover:bg-pink-600'} text-white text-xs font-bold rounded-xl transition-colors cursor-pointer whitespace-nowrap`}
            >
              <i className="ri-calendar-check-line text-sm" />
              Add to Calendar
            </a>
            <Link
              to="/events"
              className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-right-up-line text-sm" />
              View Events
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export type { RsvpRecord };
